import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import ReactTable from "react-table";
import "react-table/react-table.css";
import moment from "moment";
import Api from "api/Api";
import Loader from "components/common/Loader";
import Toaster from "components/common/Toaster";
import CommonModal from "components/common/CommonModal";

const ParsedInvoices = ({ storeId, storeName }) => {
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  const toaster = useRef();

  useEffect(() => {
    if (storeId) fetchInvoices();
  }, [storeId]);

  const fetchInvoices = () => {
    setLoader(true);
    Api.get(`admin/parsedInvoice/list?storesId=${storeId}`)
      .then(res => {
        setData(res.data.data || []);
        setLoader(false);
      })
      .catch(err => {
        if (toaster.current)
          toaster.current.error("Failed to load parsed invoices");
        setLoader(false);
      });
  };

  const columns = [
    { Header: "Order #", accessor: "orderNumber", width: 90 },
    { Header: "Invoice Date", accessor: "invoiceDate", width: 110 },
    { Header: "Bill Name", accessor: "billName", width: 160 },
    { Header: "City", accessor: "billCity", width: 100 },
    { Header: "Phone", accessor: "phone1", width: 120 },
    {
      Header: "Amount",
      accessor: "amount",
      width: 90,
      Cell: r => (r.value ? `€${r.value}` : "—")
    },
    {
      Header: "COD",
      accessor: "codAmount",
      width: 80,
      Cell: r => (r.value ? `€${r.value}` : "—")
    },
    { Header: "Type", accessor: "invoiceType", width: 120 },
    { Header: "Salesperson", accessor: "salesperson", width: 110 },
    { Header: "Customer Email", accessor: "customerEmail", width: 190 },
    {
      Header: "Invoice Email",
      accessor: "invoiceEmail",
      width: 190,
      Cell: r => r.value || <span style={{ color: "#aaa" }}>—</span>
    },
    {
      Header: "Consent",
      accessor: "consentAccepted",
      width: 80,
      Cell: r =>
        r.value ? (
          <span style={{ color: "#28a745", fontWeight: 600 }}>✓ Yes</span>
        ) : (
          <span style={{ color: "#6c757d" }}>No</span>
        )
    },
    { Header: "Branch", accessor: "branchName", width: 110 },
    { Header: "Device Login", accessor: "deviceLogin", width: 160 },
    { Header: "Parser", accessor: "parserSource", width: 70 },
    {
      Header: "Confidence",
      accessor: "confidence",
      width: 90,
      Cell: r => (r.value != null ? `${Math.round(r.value * 100)}%` : "—")
    },
    {
      Header: "Webhook",
      accessor: "webhookFired",
      width: 90,
      Cell: r =>
        r.value ? (
          <span style={{ color: "#28a745" }}>✓ {r.original.webhookStatus}</span>
        ) : (
          <span style={{ color: "#6c757d" }}>Not fired</span>
        )
    },
    {
      Header: "Parsed At",
      accessor: "parsedAt",
      width: 150,
      Cell: r => (r.value ? moment(r.value).format("DD/MM/YY HH:mm") : "—")
    },
    {
      Header: "Details",
      width: 80,
      Cell: r => (
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => {
            setSelected(r.original);
            setDetailModal(true);
          }}
        >
          View
        </button>
      )
    }
  ];

  return (
    <div className="roe-card-style mtb-15">
      <div className="roe-card-header module-header">
        <div className="flex-1 fs-16 demi-bold-text">
          <span className="hash"># </span>
          Parsed Invoices {storeName ? `— ${storeName}` : ""}
        </div>
        <button className="btn btn-primary btn-sm" onClick={fetchInvoices}>
          Refresh
        </button>
      </div>
      <div className="roe-card-body">
        <ReactTable
          data={data}
          columns={columns}
          defaultPageSize={20}
          className="-striped -highlight custom-react-table-theme-class"
          loading={loader}
          LoadingComponent={Loader}
          sortable
          filterable
          defaultFilterMethod={(filter, row) =>
            String(row[filter.id])
              .toLowerCase()
              .includes(filter.value.toLowerCase())
          }
        />
      </div>
      <Toaster ref={toaster} />
      <CommonModal
        modal={detailModal}
        toggle={() => setDetailModal(!detailModal)}
        size="xl"
        viewModalCheck
        headerData="Invoice Detail"
        children={selected ? <InvoiceDetailPanel invoice={selected} /> : null}
      />
    </div>
  );
};

const InvoiceDetailPanel = ({ invoice }) => {
  let items = [];
  try {
    items = JSON.parse(invoice.itemsJson || "[]");
  } catch (_) {}

  return (
    <div style={{ padding: "10px" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px 24px",
          marginBottom: 16
        }}
      >
        {[
          ["Order Number", invoice.orderNumber],
          ["Invoice Date", invoice.invoiceDate],
          ["Invoice Type", invoice.invoiceType],
          ["Salesperson", invoice.salesperson],
          ["Bill Name", invoice.billName],
          [
            "Bill Address",
            [invoice.billAddress1, invoice.billAddress2, invoice.billCity]
              .filter(Boolean)
              .join(", ")
          ],
          ["Phone", invoice.phone1],
          ["Customer Email (QR Scan)", invoice.customerEmail],
          ["Invoice Email (PDF)", invoice.invoiceEmail || null],
          ["Amount", invoice.amount ? `€${invoice.amount}` : "—"],
          ["COD Amount", invoice.codAmount ? `€${invoice.codAmount}` : "—"],
          ["Paid Amount", invoice.paidAmount ? `€${invoice.paidAmount}` : "—"],
          ["Paid Method", invoice.paidMethod],
          ["Store", invoice.storeName],
          ["Branch", invoice.branchName],
          ["Device Login", invoice.deviceLogin],
          [
            "Parser",
            `${invoice.parserSource} (${
              invoice.confidence
                ? Math.round(invoice.confidence * 100) + "%"
                : "?"
            })`
          ],
          ["Consent", invoice.consentAccepted ? "✓ Accepted" : "Not accepted"],
          [
            "Webhook",
            invoice.webhookFired
              ? `✓ HTTP ${invoice.webhookStatus}`
              : "Not fired"
          ]
        ].map(([k, v]) => (
          <div key={k}>
            <strong style={{ color: "#555" }}>{k}:</strong>{" "}
            <span>{v || "—"}</span>
          </div>
        ))}
        {invoice.pdfUrl && (
          <div style={{ gridColumn: "1 / -1", marginTop: 4 }}>
            <strong style={{ color: "#555" }}>PDF Receipt:</strong>{" "}
            <a
              href={invoice.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1a73e8", wordBreak: "break-all" }}
            >
              📄 {invoice.pdfUrl}
            </a>
          </div>
        )}
      </div>
      {invoice.invoiceNotes && (
        <div style={{ marginBottom: 12 }}>
          <strong>Notes:</strong> {invoice.invoiceNotes}
        </div>
      )}
      {invoice.consentMessage && (
        <div style={{ marginBottom: 12, fontSize: 12, color: "#666" }}>
          <strong>Consent Message:</strong> {invoice.consentMessage}
        </div>
      )}
      {items.length > 0 && (
        <div>
          <strong>Line Items:</strong>
          <table className="table table-sm table-bordered mt-2">
            <thead>
              <tr>
                <th>Model</th>
                <th>Manf</th>
                <th>Dprtmt</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Line Total</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td>{it.Model}</td>
                  <td>{it.Manf || "—"}</td>
                  <td>{it.Dprtmt || "—"}</td>
                  <td>{it.Description}</td>
                  <td>{it.Quantity}</td>
                  <td>€{it["Unit Price"]}</td>
                  <td>€{it["Line Total"]}</td>
                  <td>{it.Comment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  ...state.themeChanger,
  ...state.storeID
});

export default connect(mapStateToProps)(ParsedInvoices);
